// @flow

import * as React from 'react';
import { Box, useSystem } from 'react-system';
import { Paper } from '@material-ui/core';
import { useViewportType } from '../../hooks/viewport-type';
import { useWindowSize } from '../../hooks/measure';
import { useIsMobile } from '../../hooks/mobile';

type Props = {|
  children: React.Node,
  dialogTitle?: React.Node,
  actions?: React.Node,
  minWidth: number,
  paperMargin?: number,
|};

const isTouchDevice = () => {
  return (
    document.documentElement != null &&
    'ontouchstart' in document.documentElement
  );
};

export const FilterDialog = (props: Props): React.Node => {
  const mainRef = React.useRef(null);
  const [mouseOver, setMouseOver] = React.useState(false);
  const viewportType = useViewportType();
  const windowSize = useWindowSize();
  const isMobile = useIsMobile();
  const { media } = useSystem();

  React.useEffect(() => {
    const mainElt = mainRef.current;
    if (mainElt == null) {
      return;
    }
    if (mouseOver || isMobile === true) {
      const doc = document.documentElement;
      if (doc == null) {
        return;
      }
      const hasScrollbar = doc.clientHeight < doc.scrollHeight;
      const scrollTop = doc.scrollTop;
      let cssText = 'position:fixed; width: 100%;';
      if (hasScrollbar) {
        cssText += 'overflow-y: scroll;';
        cssText += `top: ${-scrollTop}px;`;
      }
      // Without raf I see flickering on safari
      requestAnimationFrame(() => {
        doc.style.cssText = cssText;
      });
      return () => {
        // Without raf I see flickering on every browser
        requestAnimationFrame(() => {
          doc.style.cssText = '';
          doc.scrollTo(0, scrollTop);
        });
      };
    }
  });

  // On mobile 100vh is the height of a screen without mobile browser header (query string) and
  // footer (on ios with buttons).
  // So if we want dialog over the whole view
  // we have that with 100vh dialog can sometimes have view height
  // but with headers dialog exceeds view on that header-footer sizes.
  // like users really see 400px but 100vh=520px
  // In such case content at dialog bottom is hidden.
  //
  // With 100% height is another issue,
  // it's the minimal height of a view when all headers are shown.
  // So without headers it will be less than window height
  //
  // The only way I know how to avoid this is to use innerHeight as it always
  // has exactly view height
  const maxHeight =
    windowSize.innerHeight === 0 ? '100vh' : `${windowSize.innerHeight}px`;

  return (
    <Paper
      elevation={6}
      onMouseEnter={() => {
        // IOS fix
        if (viewportType === 'mobile' && isTouchDevice()) {
          return;
        }
        setMouseOver(true);
      }}
      onMouseLeave={() => {
        // IOS fix
        if (viewportType === 'mobile' && isTouchDevice()) {
          return;
        }
        setMouseOver(false);
      }}
      css={[
        {
          margin: `${props.paperMargin ?? '8px'} 0`,
          maxWidth: '100vw', // `calc(100vw - ${PADDING_X * 2 + sizeOfScrollbar}px )`,
          maxHeight,
          display: 'flex',
          flexDirection: 'column',
          minWidth: props.minWidth,
        },
        // Needed for IOS-10
        media({
          minHeight: ['100%', 'auto'],
        }),
      ]}
    >
      {props.dialogTitle}

      <Box
        ref={mainRef}
        css={[
          {
            overflowY: 'auto',
            overscrollBehaviorY: 'contain',
            flex: 1,
            WebkitOverflowScrolling: 'touch',
            position: 'relative',
          },
        ]}
      >
        {props.children}
      </Box>

      {props.actions}
    </Paper>
  );
};
